<template>
  <section
    class="relative w-screen -mx-[calc(50vw-50%)] bg-zinc-50 text-zinc-900 dark:bg-black dark:text-zinc-50 overflow-hidden"
  >
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute inset-0 transition-colors duration-500
               bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.12),transparent_60%)]
               dark:bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.18),transparent_60%)]"></div>
    </div>

    <div class="relative mx-auto max-w-6xl px-6 md:px-10 py-16 lg:py-24">
      <header class="text-center">
        <p class="text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
          Social proof
        </p>
        <h2
          class="mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.02] tracking-tight text-zinc-900 dark:text-zinc-50"
        >
          Color stories from NexGridCo
        </h2>
        <p
          class="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-zinc-600 dark:text-zinc-400"
        >
          Fictional testimonials inspired by how designers might talk about palettes, contrast and
          launches after building their color systems with NexGridCo.
        </p>
      </header>

      <div
        class="mt-10 lg:mt-14 grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-center"
        @mouseenter="pause"
        @mouseleave="resume"
      >
        <div class="space-y-6">
          <div
            class="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800/80 bg-white/60 dark:bg-white/5 px-3 py-1.5 text-[11px]"
          >
            <span
              class="h-1.5 w-1.5 rounded-full"
              :style="{ backgroundColor: current.tint }"
            ></span>
            <span class="uppercase tracking-[0.16em] text-zinc-600 dark:text-zinc-400">
              {{ current.badge }}
            </span>
          </div>

          <div class="space-y-3">
            <h3 class="text-2xl sm:text-3xl lg:text-[2.1rem] font-semibold leading-tight">
              {{ current.title }}
            </h3>
            <p class="text-sm sm:text-[15px] leading-relaxed text-zinc-600 dark:text-zinc-400">
              {{ current.subtitle }}
            </p>
          </div>

          <p class="mt-3 text-sm sm:text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-200">
            “{{ current.quote }}”
          </p>
        </div>

        <div class="relative">
          <div
            class="relative rounded-2xl border border-white/10 bg-zinc-950/95 text-zinc-50 shadow-[0_24px_80px_rgba(15,23,42,0.85)] overflow-hidden"
          >
            <div
              class="absolute inset-0 opacity-80"
              :style="{
                background: `radial-gradient(circle at 0% 0%, ${current.tint}33 0, transparent 55%), radial-gradient(circle at 100% 100%, #22c55e22 0, transparent 55%)`
              }"
            ></div>

            <div class="relative p-5 sm:p-6 lg:p-7 space-y-5">
              <div class="flex items-start justify-between gap-3">
                <div class="flex items-center gap-3 min-w-0">
                  <div
                    class="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 via-cyan-400 to-emerald-400 text-[13px] font-semibold text-zinc-950 shadow-lg shadow-sky-500/40"
                  >
                    {{ current.post.initial }}
                  </div>
                  <div class="min-w-0">
                    <p class="text-[13px] font-medium text-zinc-50 truncate">
                      {{ current.post.name }}
                    </p>
                    <p class="text-[11px] text-zinc-400 truncate">
                      {{ current.post.handle }} · {{ current.post.time }}
                    </p>
                  </div>
                </div>
                <span
                  class="inline-flex items-center gap-1 rounded-full bg-zinc-900/70 px-2.5 py-1 text-[10px] text-zinc-200 border border-white/10"
                >
                  <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                  Using NexGridCo
                </span>
              </div>

              <div class="space-y-3">
                <p class="text-[13px] leading-relaxed text-zinc-100">
                  {{ current.post.text }}
                </p>

                <div
                  class="mt-3 rounded-xl border border-white/10 bg-zinc-900/80 overflow-hidden"
                >
                  <div class="px-3 pt-3 pb-2 flex items-center justify-between gap-3">
                    <div class="min-w-0">
                      <p class="text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                        Palette snapshot
                      </p>
                      <p class="mt-1 text-[12px] text-zinc-100 truncate">
                        {{ current.name }}
                      </p>
                    </div>
                    <span
                      class="rounded-full bg-zinc-800/80 px-2.5 py-1 text-[10px] text-zinc-300 border border-white/10"
                    >
                      {{ current.tag }}
                    </span>
                  </div>
                  <div class="px-3 pb-3">
                    <div class="flex gap-1.5">
                      <div
                        v-for="c in current.palette"
                        :key="c"
                        class="flex-1 h-9 rounded-md border border-white/10"
                        :style="{ backgroundColor: c }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="flex items-center justify-between gap-4 text-[11px] text-zinc-400"
              >
                <div class="flex items-center gap-3">
                  <span
                    v-for="stat in current.post.stats"
                    :key="stat.label"
                    class="flex items-center gap-1.5"
                  >
                    <span
                      class="h-1.5 w-1.5 rounded-full"
                      :style="{ backgroundColor: current.tint }"
                    ></span>
                    <span class="font-medium text-zinc-100">{{ stat.value }}</span>
                    <span class="text-zinc-400">{{ stat.label }}</span>
                  </span>
                </div>
                <span class="shrink-0 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  Color testimonial
                </span>
              </div>
            </div>
          </div>

          <button
            @click="prev"
            class="hidden md:flex absolute -left-6 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full border border-zinc-200/70 bg-white/80 text-zinc-700 shadow-sm hover:bg-white hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/90 dark:text-zinc-100 dark:hover:bg-zinc-900 transition"
            aria-label="Previous"
          >
            ‹
          </button>
          <button
            @click="next"
            class="hidden md:flex absolute -right-6 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full border border-zinc-200/70 bg-white/80 text-zinc-700 shadow-sm hover:bg-white hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/90 dark:text-zinc-100 dark:hover:bg-zinc-900 transition"
            aria-label="Next"
          >
            ›
          </button>
        </div>
      </div>

      <div class="mt-10 flex flex-wrap items-center justify-center gap-3">
        <button
          v-for="(shot, i) in testimonials"
          :key="shot.id"
          @click="go(i)"
          class="flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11px] transition-all"
          :class="
            i === active
              ? 'border-zinc-900 bg-zinc-900 text-zinc-50 dark:border-zinc-50 dark:bg-zinc-50 dark:text-zinc-900 shadow-sm'
              : 'border-zinc-200 bg-white/60 text-zinc-500 hover:text-zinc-900 hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-400 dark:hover:text-zinc-100'
          "
        >
          <span
            class="h-1.5 w-1.5 rounded-full"
            :style="{ backgroundColor: shot.tint }"
          ></span>
          <span>{{ shot.label }}</span>
        </button>
      </div>

      <section class="mt-16 flex flex-col items-center gap-5 text-center">
        <p class="text-sm text-zinc-600 dark:text-zinc-400 max-w-md leading-relaxed">
          Want to be featured here? Tag
          <span class="font-semibold text-zinc-900 dark:text-zinc-50">@nexgridco</span>
          on X when you share a palette, UI, or launch using NexGridCo.
        </p>

        <div
          class="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 bg-white/80 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-600 shadow-sm dark:border-zinc-700/80 dark:bg-zinc-900/80 dark:text-zinc-300"
        >
          Public
        </div>

        <a
          href="https://x.com/nexgridco"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 rounded-2xl border border-zinc-300/60 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 dark:border-zinc-700/70 dark:bg-zinc-900/80 dark:text-zinc-300 dark:hover:border-zinc-50 dark:hover:text-zinc-50"
        >
          <span class="material-symbols-rounded text-[18px]">public</span>
          Follow on X
        </a>
      </section>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const active = ref(0)
let autoplay

const testimonials = [
  {
    id: 'yu',
    label: '@Yu****',
    name: 'Deep SaaS teal palette',
    title: '“My dashboard finally looks intentional, not patched together”',
    subtitle:
      'A product designer aligns brand, dashboard and marketing colors in one place.',
    badge: 'Product designer',
    tint: '#0ea5e9',
    quote:
      'I stopped guessing hex codes. Seeing all my states, surfaces and accents together made everything click.',
    tag: 'Product UI',
    palette: ['#020617', '#0EA5E9', '#22C55E', '#FACC15'],
    post: {
      name: 'Yu ****',
      handle: '@Yu****',
      initial: 'Y',
      time: '3h ago',
      text: 'Color sign-off used to be the most stressful part of every release. With NexGridCo, it feels like part of the design flow instead of a last-minute fire drill.',
    }
  },
  {
    id: 'da',
    label: '@Da****',
    name: 'Contrast-first workspace',
    title: '“Contrast talks are no longer a fight with devs”',
    subtitle:
      'A UI designer checks headings, body text and states against real surfaces.',
    badge: 'UI & contrast',
    tint: '#4f46e5',
    quote:
      'When I show the contrast view from NexGridCo, the conversation shifts from opinion to clarity.',
    tag: 'Accessibility lab',
    palette: ['#020617', '#F9FAFB', '#4F46E5', '#F97316'],
    post: {
      name: 'Da ****',
      handle: '@Da****',
      initial: 'D',
      time: 'Yesterday',
      text: 'I just drop my palette in, toggle themes and see which pairs hold up. No more separate spreadsheets or guesswork.',
    }
  },
  {
    id: 'mi',
    label: '@Mi****',
    name: 'Brand kit + UI set',
    title: '“One palette that works for hero shots and real screens”',
    subtitle:
      'A freelance designer keeps brand, marketing and in-product palettes connected.',
    badge: 'Freelance designer',
    tint: '#ec4899',
    quote:
      'I used to keep a “pretty” palette and a “usable” one. Now it’s the same kit, with logic baked in.',
    tag: 'Brand kit',
    palette: ['#020617', '#F9FAFB', '#EC4899', '#F97316'],
    post: {
      name: 'Mi ****',
      handle: '@Mi****',
      initial: 'M',
      time: 'This week',
      text: 'Showing clients their brand colors inside real UI previews from NexGridCo made feedback 10× easier. They react to screens, not swatches.',
    }
  },
  {
    id: 'sa',
    label: '@Sa****',
    name: 'Semantic token grid',
    title: '“Changing one token doesn’t scare me anymore”',
    subtitle:
      'A design systems designer keeps semantic tokens mapped cleanly to real colors.',
    badge: 'Design systems',
    tint: '#22c55e',
    quote:
      'I update roles in NexGridCo, export, and everything stays in sync. No more mystery hex codes.',
    tag: 'Tokens',
    palette: ['#22C55E', '#0EA5E9', '#F97316', '#EF4444'],
    post: {
      name: 'Sa ****',
      handle: '@Sa****',
      initial: 'S',
      time: '2 days ago',
      text: 'Seeing semantic roles, raw hex values and exports in the same place made our token story finally feel coherent.',
    }
  },
  {
    id: 'lu',
    label: '@Lu****',
    name: 'Launch-ready palettes',
    title: '“Color is no longer the blocker on launch week”',
    subtitle:
      'A solo builder sets palettes, checks contrast and reuses the same kit across launches.',
    badge: 'Solo builder',
    tint: '#f97316',
    quote:
      'I used to tweak colors the night before launch. Now I lock the palette early and reuse it everywhere.',
    tag: 'Launch kit',
    palette: ['#020617', '#0F172A', '#F97316', '#22C55E'],
    post: {
      name: 'Lu ****',
      handle: '@Lu****',
      initial: 'L',
      time: '4h ago',
      text: 'Having palettes, contrast checks and UI previews in one place lets me focus on the story, not on hunting hex codes.',
    }
  }
]

const current = computed(() => testimonials[active.value])

const next = () => {
  active.value = (active.value + 1) % testimonials.length
}

const prev = () => {
  active.value = (active.value - 1 + testimonials.length) % testimonials.length
}

const go = i => {
  active.value = i
}

const play = () => {
  autoplay = setInterval(next, 9000)
}

const pause = () => {
  if (autoplay) {
    clearInterval(autoplay)
    autoplay = null
  }
}

const resume = () => {
  if (!autoplay) play()
}

onMounted(play)
onBeforeUnmount(pause)
</script>
